<template>
  <div class="cust-page">
    <header class="cust-header">
      <button type="button" class="cust-back" @click="goBack">‹ Administration</button>
      <h1 class="cust-title">Customization</h1>
      <p class="cust-lede">Customize app behavior for your organization.</p>
    </header>

    <div v-if="!canManage" class="cust-denied">
      You do not have permission to manage customization.
    </div>

    <nav v-else class="cust-menu" aria-label="Customization menu">
      <button type="button" class="cust-menu-item" @click="showSchedulingContextModal = true">
        <span class="cust-menu-item__body">
          <span class="cust-menu-item__title">In-Home Visit Context</span>
          <span class="cust-menu-item__hint">Add custom Visit Context values for scheduling appointments</span>
        </span>
        <span class="cust-menu-item__arrow" aria-hidden="true">›</span>
      </button>
    </nav>

    <SchedulingContextCustomizationModal
      v-if="showSchedulingContextModal"
      :accountnumber="accountnumber"
      :actor-id="currentUserId"
      :actor-name="currentUserName"
      @close="showSchedulingContextModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { sessionActorFrom } from "@/utils/sessionIdentity";
import { PRIVILEGES } from "@/constants/privileges";
import SchedulingContextCustomizationModal from "./SchedulingContextCustomizationModal.vue";

export default {
  name: "CustomizationPage",
  components: { SchedulingContextCustomizationModal },
  data() {
    return {
      showSchedulingContextModal: false,
    };
  },
  computed: {
    ...mapGetters("login", { allData: "getAllData" }),
    sessionActor() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile);
    },
    accountnumber() {
      return this.sessionActor.accountnumber;
    },
    currentUserId() {
      return this.sessionActor.unique_ID;
    },
    currentUserName() {
      return this.sessionActor.displayName || "Unknown user";
    },
    canManage() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.ADJUST_PERMISSIONS);
    },
  },
  methods: {
    goBack() {
      this.$router.push("/administration");
    },
  },
};
</script>

<style scoped lang="scss">
$admin-navy: #173a63;
$admin-border: #e2e8f0;
$admin-muted: #64748b;

.cust-page {
  max-width: 36rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 2rem;
}

.cust-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $admin-border;
  text-align: center;
  position: relative;
}

.cust-back {
  position: absolute;
  left: 0;
  top: 0;
  border: none;
  background: transparent;
  color: $admin-muted;
  font-size: 0.9rem;
  cursor: pointer;
}
.cust-back:hover { color: $admin-navy; }

.cust-title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 700;
  color: $admin-navy;
  letter-spacing: -0.02em;
}

.cust-lede {
  margin: 0.4rem 0 0;
  font-size: 0.95rem;
  color: $admin-muted;
}

.cust-denied {
  padding: 1rem 1.1rem;
  border: 1px solid #f5c2c2;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 12px;
  font-size: 0.95rem;
}

.cust-menu {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.cust-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  text-align: left;
  padding: 0.95rem 1.1rem;
  border: 1px solid $admin-border;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #c5d0e0;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
    background: #fafbff;
  }

  &:focus-visible {
    outline: 2px solid #5a7ab0;
    outline-offset: 2px;
  }
}

.cust-menu-item__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.cust-menu-item__title {
  font-size: 1.05rem;
  font-weight: 600;
  color: $admin-navy;
}

.cust-menu-item__hint {
  font-size: 0.82rem;
  color: $admin-muted;
  line-height: 1.35;
}

.cust-menu-item__arrow {
  flex-shrink: 0;
  font-size: 1.35rem;
  font-weight: 300;
  color: #7f96b8;
  line-height: 1;
}
</style>
